import { useState, useRef, useEffect } from 'react';
import { ArrowUp, Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/cn';
import { ViewModeToggle } from '@/components/ui/ViewModeToggle';
import type { ViewMode } from '@/hooks/useViewMode';
import type { ShowType } from './utils';

const SORT_CRITERIA = ['Date', 'Name', 'Type'];

interface FolderHeaderProps {
  sortCriteriaIndex: number;
  sortAscending: boolean;
  showTypes: ShowType[];
  onSortCriteriaChange: () => void;
  onSortDirectionToggle: () => void;
  onShowTypesChange: (types: ShowType[]) => void;
  showTypeFilter: boolean;
  disabled: boolean;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
}

export function FolderHeader({
  sortCriteriaIndex,
  sortAscending,
  showTypes,
  onSortCriteriaChange,
  onSortDirectionToggle,
  onShowTypesChange,
  showTypeFilter,
  disabled,
  viewMode,
  onViewModeChange,
}: FolderHeaderProps) {
  return (
    <div className="flex flex-wrap items-center justify-between border-b border-border/60 pb-3 mb-4">
      {/* Sort section */}
      <div className="flex shrink-0 items-center gap-2">
        <span className="text-sm font-semibold text-muted-foreground">Sort by:</span>
        <button
          onClick={onSortCriteriaChange}
          disabled={disabled}
          className="text-sm font-bold text-foreground hover:text-muted-foreground disabled:opacity-50"
        >
          {SORT_CRITERIA[sortCriteriaIndex]}
        </button>
        <div className="h-4 border-l border-muted-foreground" />
        <button
          onClick={onSortDirectionToggle}
          disabled={disabled}
          title={sortAscending ? 'ascending' : 'descending'}
          className="transition-transform disabled:opacity-50"
        >
          <ArrowUp
            className={cn(
              'h-3 w-3 text-foreground transition-transform',
              !sortAscending && 'rotate-180',
            )}
          />
        </button>
      </div>

      <div className="flex items-center gap-2">
        {/* Type filter section */}
        {showTypeFilter && (
          <TypeFilterDropdown
            showTypes={showTypes}
            onChange={onShowTypesChange}
            disabled={disabled}
          />
        )}
        {viewMode && onViewModeChange && (
          <ViewModeToggle mode={viewMode} onChange={onViewModeChange} />
        )}
      </div>
    </div>
  );
}

// ── Type Filter Dropdown ────────────────────────────────────────────

interface TypeFilterDropdownProps {
  showTypes: ShowType[];
  onChange: (types: ShowType[]) => void;
  disabled: boolean;
}

function TypeFilterDropdown({ showTypes, onChange, disabled }: TypeFilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const allSelected = showTypes.every((t) => !t.filterStatus);
  const noneSelected = showTypes.every((t) => t.filterStatus);

  const title = allSelected
    ? 'All'
    : noneSelected
      ? 'None'
      : showTypes
          .filter((t) => !t.filterStatus)
          .map((t) => (t.label === 'doc' ? 'documents' : `${t.label}s`))
          .join(', ');

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [open]);

  function handleTypeClick(index: number) {
    let arr: ShowType[];
    if (allSelected) {
      // First click when "All" is selected: hide everything except clicked type
      arr = showTypes.map((t) => ({ ...t, filterStatus: true }));
    } else {
      arr = showTypes.map((t) => ({ ...t }));
    }
    arr[index].filterStatus = !arr[index].filterStatus;
    onChange(arr);
  }

  function toggleAll() {
    if (allSelected) {
      onChange(showTypes.map((t) => ({ ...t, filterStatus: true })));
    } else {
      onChange(showTypes.map((t) => ({ ...t, filterStatus: false })));
    }
  }

  return (
    <div ref={ref} className="relative flex items-center gap-2">
      <span className="text-sm font-semibold text-muted-foreground">Show file types:</span>
      <button
        onClick={() => !disabled && setOpen(!open)}
        className={cn(
          'flex w-36 items-center justify-between rounded-full border border-border bg-secondary px-3 py-1.5',
          disabled && 'opacity-50',
        )}
      >
        <span className="truncate text-left text-sm font-semibold capitalize">{title}</span>
        <ChevronDown
          className={cn('h-3.5 w-3.5 text-muted-foreground transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="absolute right-0 top-10 z-10 w-36 rounded-xl border border-border/60 bg-card py-1 shadow-xl">
          {showTypes.map((type, index) => {
            const isActive = !type.filterStatus && !allSelected;
            return (
              <button
                key={type.label}
                onClick={() => handleTypeClick(index)}
                className="flex w-full items-center gap-2 px-3 py-1 text-sm capitalize hover:bg-secondary"
              >
                <Check
                  className={cn(
                    'h-3.5 w-3.5',
                    isActive ? 'text-primary' : 'text-transparent',
                  )}
                />
                {type.label === 'doc' ? 'documents' : `${type.label}s`}
              </button>
            );
          })}
          <div className="mx-2 my-1 border-t" />
          <button
            onClick={toggleAll}
            className="flex w-full items-center gap-2 px-3 py-1 text-sm hover:bg-secondary"
          >
            <Check
              className={cn(
                'h-3.5 w-3.5',
                allSelected ? 'text-primary' : 'text-transparent',
              )}
            />
            All
          </button>
        </div>
      )}
    </div>
  );
}
